/**
 * CasperBridgeQueue — глобальная очередь вызовов нативного Bridge.
 *
 * Гарантирует строго последовательное выполнение: следующий вызов начинается
 * только после завершения (resolve / reject / timeout) предыдущего.
 *
 * Использование:
 *   import { bridgeQueue } from '@/lib/casper-bridge-manager'
 *   const result = await bridgeQueue.enqueue('init', [], () => window.CasperBridge!.init())
 */

/** Таймаут для большинства Bridge-вызовов (мс). */
export const BRIDGE_TIMEOUT_MS = 15_000

/**
 * Таймаут для вызовов, требующих действия пользователя (камера, файловый пикер).
 * Пользователь может долго выбирать файл или записывать видео.
 */
export const BRIDGE_USER_ACTION_TIMEOUT_MS = 5 * 60 * 1_000

// ─── Внутренние типы ──────────────────────────────────────────────────────────

interface PendingTask {
  name: string
  logArgs: unknown[]
  run: () => Promise<unknown>
  resolve: (val: unknown) => void
  reject: (err: unknown) => void
  timeoutMs: number
}

// ─── Очередь ─────────────────────────────────────────────────────────────────

class CasperBridgeQueue {
  private queue: PendingTask[] = []
  private processing = false

  /**
   * Помещает Bridge-вызов в очередь.
   *
   * @param name      Имя метода (для логов): 'init', 'login', 'AR.open' и т.д.
   * @param logArgs   Аргументы для вывода в консоль. НЕ передавайте полный токен — только last-N символов.
   * @param fn        Функция, выполняющая нативный вызов.
   * @param timeoutMs Таймаут ожидания ответа. По умолчанию BRIDGE_TIMEOUT_MS (15 с).
   */
  enqueue<T>(
    name: string,
    logArgs: unknown[],
    fn: () => Promise<T>,
    timeoutMs = BRIDGE_TIMEOUT_MS,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const waitingCount = this.queue.length + (this.processing ? 1 : 0)
      if (waitingCount > 0) {
        console.log(`[Bridge] ⏳ "${name}" ожидает в очереди (${waitingCount} перед ним)`)
      }

      this.queue.push({
        name,
        logArgs,
        run: fn as () => Promise<unknown>,
        resolve: resolve as (v: unknown) => void,
        reject,
        timeoutMs,
      })

      if (!this.processing) this.next()
    })
  }

  private next() {
    const task = this.queue.shift()
    if (!task) {
      this.processing = false
      return
    }

    this.processing = true
    const start = Date.now()

    console.group(`[Bridge] ▶ "${task.name}"`)
    console.log('timestamp :', new Date().toISOString())
    if (task.logArgs.length > 0) console.log('args      :', ...task.logArgs)
    console.log('в очереди :', this.queue.length, '(после этого)')
    console.groupEnd()

    let settled = false

    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      const msg = `Bridge timeout: "${task.name}" не ответил за ${task.timeoutMs}ms`
      console.error(`[Bridge] ⏱ ${msg}`)
      task.reject(new Error(msg))
      this.next()
    }, task.timeoutMs)

    task
      .run()
      .then(result => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        const ms = Date.now() - start
        console.group(`[Bridge] ✓ "${task.name}" (${ms}ms)`)
        console.log('timestamp:', new Date().toISOString())
        console.log('result   :', result)
        console.groupEnd()
        task.resolve(result)
        this.next()
      })
      .catch(err => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        const ms = Date.now() - start
        console.group(`[Bridge] ✗ "${task.name}" (${ms}ms)`)
        console.error('timestamp:', new Date().toISOString())
        console.error('error    :', err)
        console.groupEnd()
        task.reject(err)
        this.next()
      })
  }
}

/** Глобальный синглтон очереди Bridge-вызовов. */
export const bridgeQueue = new CasperBridgeQueue()
